import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Mission, PoiClient } from '../data-management.model';
import { DataManagementComponent } from '../data-management.component';
import { DeliveryPoiDto, MissionService, RouteSegment } from '../providers';
import { MapService } from 'src/app/utils/leaflet/service/map.service';
import L, { Marker } from 'leaflet';
import { imagesDir, imagesPOIDir } from 'src/app/global.config';
import { saveAs as importedSaveAs } from 'file-saver';
declare var $: any;

@Component({
  selector: 'app-mission',
  templateUrl: './mission.component.html',
  styleUrls: ['./mission.component.scss']
})
export class MissionComponent implements OnInit {
  loading: boolean = false;
  routeLoading: boolean = false; // New loading flag for route fetching
  missions: Mission[] = [];
  selectedMission: Mission | null = null;
  selectedRoute: any | null = null; // Track selected route
  missionForm: Mission = new Mission();
  deliveryPoints: DeliveryPoiDto[] = [];
  isCollapsed = false;

  @ViewChild('missionDeleteModal', { static: false }) missionDeleteModal: ModalDirective;
  @ViewChild('missionFormModal', { static: false }) missionFormModal: ModalDirective;

  mode = 'ADD';

  constructor(
    private parent: DataManagementComponent,
    private toastr: ToastrService,
    public mapService: MapService,
    private missionService: MissionService
  ) {}

  ngOnInit() {
    this.parent.initAllIcons();
    this.loadMissions();
  }

  loadMissions() {
    this.loading = true;
    this.missionService.getMissions()
      .subscribe({
        next: missions => {
          this.loading = false;
          this.missions = missions;
          this.selectedMission = this.missions[0];
          if (this.selectedMission) {
            this.drawRoute();
          }
        },
        error: () => {
          this.loading = false;
          this.toastr.error("Erreur lors de chargements", "Mission");
        }
      });
  }

  openMissionForm() {
    this.mode = 'ADD';
    this.missionForm = new Mission();
    this.missionFormModal.show();
  }

  openMissionEditForm() {
    if (!this.selectedMission) return;
    this.mode = 'UPDATE';
    this.missionForm = Object.assign({}, this.selectedMission);
    this.missionFormModal.show();
  }

  selectedMissionList(mission: Mission) {
    this.selectedMission = mission;
    this.selectedRoute = null; // Reset selected route when changing mission
    this.drawRoute();
  }

  onMissionSaved(mission: Mission) {
    this.missionFormModal.hide();
    this.loadMissions();
  }

  onDelete() {
    if (!this.selectedMission) return;
    this.loading = true;
    this.missionService.delete(this.selectedMission.id)
      .subscribe({
        next: response => {
          this.loading = false;
          this.missionDeleteModal.hide();
          if (response) {
            this.toastr.success("Supprimé", "Mission");
            this.loadMissions();
          } else {
            this.toastr.error("Erreur lors de suppression", "Mission");
          }
        },
        error: () => {
          this.loading = false;
          this.toastr.error("Erreur lors de suppression", "Mission");
        }
      });
  }

  onConfirmDelete() {
    this.missionDeleteModal.show();
  }

  missionFormModalhide() {
    this.missionFormModal.hide();
  }

  missionDeleteModalhide() {
    this.missionDeleteModal.hide();
  }

  drawRoute() {
    if (!this.selectedMission || !this.selectedMission.routeLegs?.length) {
      this.toastr.warning("Aucun trajet à afficher", "Mission");
      return;
    }

    this.routeLoading = true;
    this.mapService.clearPolylines();
    this.mapService.removeMarkersPoiFromMap?.();
    this.mapService.removeMarkersPoiFromMapII?.();
    this.mapService.removeMarkersFromMap?.();

    const allCoords: [number, number][] = [];

    this.missionService.getRouteSegments(this.convertRouteLegToDeliveryPoi(this.selectedMission.routeLegs))
      .subscribe({
        next: cordsRoute => {
          this.routeLoading = false;
          if (!cordsRoute?.length) {
            this.toastr.warning("Aucun segment de route reçu", "Mission");
            return;
          }

          // Match RouteSegments to RouteLegs
          const enrichedSegments = cordsRoute.map(segment => ({
            ...segment,
            trajet: this.missionService.findTrajetByListTrajet(segment, this.selectedMission!.routeLegs)
          }));

          this.drawRouteSegments(enrichedSegments, allCoords);
          this.zoomToFullRoute(allCoords);
        },
        error: () => {
          this.routeLoading = false;
          this.toastr.error("Erreur lors du chargement des segments de route", "Mission");
        }
      });
  }

  drawRoutefille(trajet: any) {
    if (!trajet) {
      this.toastr.warning("Trajet invalide", "Mission");
      return;
    }

    this.selectedRoute = trajet; // Set the selected route
    this.routeLoading = true;
    this.mapService.clearPolylines();
    this.mapService.removeMarkersPoiFromMap?.();
    this.mapService.removeMarkersPoiFromMapII?.();
    this.mapService.removeMarkersFromMap?.();

    const allCoords: [number, number][] = [];

    // Fetch segment for this trajet
    this.missionService.getRouteSegments([
      { lat: trajet.latPOISource, lng: trajet.lngPOISource, name: trajet.labelPOISource },
      { lat: trajet.latPOITarget, lng: trajet.lngPOITarget, name: trajet.labelPOITarget }
    ]).subscribe({
      next: cordsRoute => {
        this.routeLoading = false;
        if (!cordsRoute?.length) {
          this.toastr.warning("Aucun segment reçu pour ce trajet", "Mission");
          return;
        }

        const segment = { ...cordsRoute[0], trajet };
        const coords: [number, number][] = segment.coordinates.map(([lng, lat]) => [lat, lng]);

        // Draw polyline with distinct color for selected route
        const polyline = L.polyline(coords, { color: '#041159ee', weight: 6, opacity: 0.9 });
        this.mapService.addPolyline(polyline);

        // Draw markers with distinct color
        const fromCoord = coords[0];
        const toCoord = coords[coords.length - 1];
        const fromPopup = `<strong>${segment.fromName}</strong><br>Distance: ${(trajet.travelDistance / 1000).toFixed(2)} Km<br>Durée: ${this.formatDuration(trajet.travelDuration)}`;
        const toPopup = `<strong>${segment.toName}</strong><br>Distance: ${(trajet.travelDistance / 1000).toFixed(2)} Km<br>Durée: ${this.formatDuration(trajet.travelDuration)}`;

        this.drawPointInterest(fromCoord, fromPopup, 'marker-selected', 1);
        this.drawPointInterest(toCoord, toPopup, 'marker-selected', 2);

        allCoords.push(...coords);
        this.zoomToFullRoute(allCoords);
      },
      error: () => {
        this.routeLoading = false;
        this.toastr.error("Erreur lors du chargement du segment de trajet", "Mission");
      }
    });
  }

  drawRouteSegments(segments: Array<RouteSegment & { trajet?: any | null }>, allCoords: [number, number][]) {
    segments.forEach((segment, i) => {
      const coords: [number, number][] = segment.coordinates.map(([lng, lat]) => [lat, lng]);

      // Draw polyline with default color
      const polyline = L.polyline(coords, { color: '#04277eb4', weight: 5 });
      this.mapService.addPolyline(polyline);

      // Draw markers
      const fromCoord = coords[0];
      const toCoord = coords[coords.length - 1];
      const fromPopup = `<strong>${segment.fromName}</strong>${
        segment.trajet ? `<br>Distance: ${(segment.trajet.travelDistance / 1000).toFixed(2)} Km<br>Durée: ${this.formatDuration(segment.trajet.travelDuration)}` : ''
      }`;
      const toPopup = `<strong>${segment.toName}</strong>${
        segment.trajet ? `<br>Distance: ${(segment.trajet.travelDistance / 1000).toFixed(2)} Km<br>Durée: ${this.formatDuration(segment.trajet.travelDuration)}` : ''
      }`;

      this.drawPointInterest(fromCoord, fromPopup, this.getGpsMarkerColorClass(i), i + 1);
      this.drawPointInterest(toCoord, toPopup, this.getGpsMarkerColorClass(i), i + 1);

      allCoords.push(...coords);
    });
  }

  drawPointInterest(latlng: [number, number], popupContent: string, colorClass: string, num: number) {
    const marker = new L.Marker(latlng);
    const svg = this.generateSvgWithNumber(num);
    marker.setIcon(
      L.divIcon({
        className: `custom-marker ${colorClass}`,
        html: svg,
        iconAnchor: [15, 30],
        popupAnchor: [0, -25]
      })
    );

    marker.bindPopup(popupContent);
    marker.on('click', () => this.mapService.map.setView(latlng, 17));
    this.mapService.addPoiMarkerII(marker);
  }

  zoomToFullRoute(routeCoordinates: [number, number][]) {
    if (!routeCoordinates?.length) return;

    const latLngs = routeCoordinates.map(([lat, lng]) => L.latLng(lat, lng));
    const bounds = L.latLngBounds(latLngs);
    this.mapService.map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 16
    });
  }

  getGpsMarkerColorClass(index: number): string {
    const colorClasses = [
      'marker-green', 'marker-blue', 'marker-purple', 'marker-yellow', 'marker-pink',
      'marker-cyan', 'marker-lime', 'marker-teal', 'marker-indigo', 'marker-brown',
      'marker-gray', 'marker-darkgray', 'marker-gold', 'marker-silver', 'marker-maroon',
      'marker-olive', 'marker-navy', 'marker-magenta', 'marker-sky', 'marker-rose',
      'marker-mint', 'marker-coral', 'marker-turquoise', 'marker-azure', 'marker-beige',
      'marker-mustard', 'marker-salmon', 'marker-violet', 'marker-caramel', 'marker-amber',
      'marker-lavender', 'marker-wine', 'marker-forest', 'marker-steel', 'marker-bronze',
      'marker-ruby', 'marker-peach', 'marker-cherry', 'marker-cream', 'marker-coffee',
      'marker-charcoal', 'marker-ice', 'marker-mocha', 'marker-smoke', 'marker-lemon',
      'marker-night', 'marker-ocean', 'marker-plum'
    ];
    return colorClasses[index % colorClasses.length];
  }

  generateSvgWithNumber(num: number): string {
    return `
      <svg viewBox="0 0 512 512" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
        <circle cx="255.996" cy="193.345" r="96.759" fill="currentColor" />
        <path fill="currentColor" d="M256,304.456c-61.264,0-111.107-49.842-111.107-111.105S194.735,82.244,256,82.244
          s111.107,49.842,111.107,111.107C367.107,254.615,317.265,304.456,256,304.456z M256,110.943
          c-45.439,0-82.408,36.967-82.408,82.408c0,45.439,36.969,82.406,82.408,82.406s82.408-36.967,82.408-82.406
          C338.408,147.91,301.439,110.943,256,110.943z"/>
        <path fill="currentColor" d="M256,0C149.386,0,62.649,86.737,62.649,193.351c0,72.07,39.642,135.049,98.262,168.299
          l82.662,143.175c2.563,4.44,7.301,7.175,12.427,7.175c5.126,0,9.864-2.735,12.427-7.175l84.026-145.537
          c3.963-6.863,1.611-15.639-5.252-19.601c-6.866-3.963-15.639-1.611-19.601,5.252l-71.598,124.013l-51.518-89.231
          c16.409,4.542,33.683,6.98,51.518,6.98c7.924,0,14.349-6.424,14.349-14.349c0-7.925-6.426-14.35-14.349-14.35
          c-90.791,0-164.652-73.863-164.652-164.652S165.211,28.699,256,28.699s164.652,73.863,164.652,164.652
          c0,40.902-15.099,80.118-42.515,110.421c-5.316,5.878-4.863,14.951,1.014,20.269c5.876,5.316,14.951,4.862,20.269-1.014
          c32.197-35.591,49.93-81.644,49.93-129.675C449.351,86.737,362.614,0,256,0z"/>
        <circle cx="256" cy="205" r="100" fill="#646875" />
        <text
          x="256"
          y="215"
          font-size="140"
          text-anchor="middle"
          fill="#ffffff"
          font-family="Arial, sans-serif"
          dominant-baseline="middle"
        >
          ${num}
        </text>
      </svg>
    `;
  }

  convertRouteLegToDeliveryPoi(routeLegs: any[]): DeliveryPoiDto[] {
    const poiList: DeliveryPoiDto[] = [];
    for (const leg of routeLegs) {
      poiList.push({
        lat: leg.latPOISource,
        lng: leg.lngPOISource,
        name: leg.labelPOISource
      });
    }
    if (routeLegs.length > 0) {
      const last = routeLegs[routeLegs.length - 1];
      poiList.push({
        lat: last.latPOITarget,
        lng: last.lngPOITarget,
        name: last.labelPOITarget
      });
    }
    return poiList;
  }

  private formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  downloadExcel(){
    this.loading = true;
    this.missionService.getMissionExcel(this.selectedMission.id).subscribe(
        (blob) => {            
          importedSaveAs(blob,this.selectedMission.label + '.xlsx');            
          this.loading = false;
        },
        () => {
            this.toastr.error('Error !', 'Error');
            this.loading = false;
        }
    );
  }
}